import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppSelector, useAppDispatch } from '../../store/hooks';
import { setSubscriptionTier } from '../../store/slices/userSlice';
import { CText } from '../../components/common';
import {
  FiCheck,
  FiX,
  FiArrowLeft,
  FiZap,
  FiMessageSquare,
  FiGrid,
  FiUsers,
  FiSearch,
  FiBell,
  FiHeadphones,
} from 'react-icons/fi';
import routes from '../../utils/routes';

type PlanType = 'free' | 'pro' | 'premium';

interface PlanFeature {
  name: string;
  free: string | boolean;
  pro: string | boolean;
  premium: string | boolean;
  icon: any;
}

const Pricing: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const colors = useAppSelector((state) => state.theme.theme);
  const currentTier = useAppSelector((state) => state.user.subscription.tier);

  const [selectedPlan, setSelectedPlan] = useState<PlanType>('pro');
  const [isProcessing, setIsProcessing] = useState(false);

  const plans = [
    {
      id: 'free' as PlanType,
      name: 'Free',
      price: 0,
      period: 'forever',
      description: 'Get started with basic features',
      highlight: false,
    },
    {
      id: 'pro' as PlanType,
      name: 'Pro',
      price: 499,
      period: '/month',
      description: 'Unlock the full power of Yantrai',
      highlight: true,
      badge: 'Most Popular',
    },
    {
      id: 'premium' as PlanType,
      name: 'Premium',
      price: 999,
      period: '/month',
      description: 'For serious investors',
      highlight: false,
    },
  ];

  const features: PlanFeature[] = [
    {
      name: 'AI Copilot Queries',
      icon: FiMessageSquare,
      free: '5/week',
      pro: 'Unlimited',
      premium: 'Unlimited',
    },
    {
      name: 'Deep Search (Full PDFs)',
      icon: FiSearch,
      free: false,
      pro: true,
      premium: true,
    },
    {
      name: 'Investment Buckets',
      icon: FiGrid,
      free: '2 buckets',
      pro: 'All 15+ buckets',
      premium: 'All + alerts',
    },
    {
      name: 'Bucket Price Alerts',
      icon: FiBell,
      free: false,
      pro: false,
      premium: true,
    },
    {
      name: 'Expert Advisor Discount',
      icon: FiUsers,
      free: false,
      pro: '10% off',
      premium: '25% off + 1 free',
    },
    {
      name: 'Priority Support',
      icon: FiHeadphones,
      free: false,
      pro: false,
      premium: true,
    },
  ];

  const handleSelectPlan = async (planId: PlanType) => {
    if (planId === currentTier) return;

    setSelectedPlan(planId);
    setIsProcessing(true);

    // TODO: Integrate with Razorpay for actual payment
    // For now, just simulate the upgrade
    setTimeout(() => {
      dispatch(setSubscriptionTier(planId));
      setIsProcessing(false);
      navigate(routes.home);
    }, 1500);
  };

  const renderFeatureValue = (value: string | boolean) => {
    if (typeof value === 'boolean') {
      return value ? (
        <div
          className="w-6 h-6 rounded-full flex items-center justify-center"
          style={{ backgroundColor: colors.primary + '20' }}
        >
          {React.createElement(FiCheck as any, { size: 14, color: colors.primary })}
        </div>
      ) : (
        <div
          className="w-6 h-6 rounded-full flex items-center justify-center"
          style={{ backgroundColor: colors.grayScale400 + '30' }}
        >
          {React.createElement(FiX as any, { size: 14, color: colors.grayScale400 })}
        </div>
      );
    }
    return (
      <CText type="r12" color={colors.textColor} style={{ fontWeight: 500 }}>
        {value}
      </CText>
    );
  };

  return (
    <div
      className="min-h-screen pb-24"
      style={{ backgroundColor: colors.bgColor || colors.backgroundColor }}
    >
      {/* Header */}
      <div
        className="sticky top-0 z-10 px-4 py-4 border-b"
        style={{
          backgroundColor: colors.glassBackground || colors.backgroundColor,
          borderColor: colors.borderColor,
          backdropFilter: 'blur(12px)',
        }}
      >
        <div className="lg:max-w-4xl lg:mx-auto flex items-center gap-4">
          <button
            onClick={() => navigate(-1)}
            className="p-2 rounded-xl transition-all duration-200 hover:opacity-80"
            style={{ backgroundColor: colors.inputBackground }}
          >
            {React.createElement(FiArrowLeft as any, { size: 20, color: colors.textColor })}
          </button>
          <div>
            <CText type="B18" color={colors.textColor}>
              Choose Your Plan
            </CText>
            <CText type="r12" color={colors.grayScale500}>
              Unlock premium features
            </CText>
          </div>
        </div>
      </div>

      <div className="p-4 lg:p-8 lg:max-w-4xl lg:mx-auto">
        {/* Hero */}
        <div className="text-center mb-8">
          <div
            className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4"
            style={{
              background: `linear-gradient(135deg, ${colors.primary}, #9F03F2)`,
              boxShadow: colors.shadowGlow,
            }}
          >
            {React.createElement(FiZap as any, { size: 32, color: '#fff' })}
          </div>
          <h1 className="text-2xl font-bold gradient-text mb-2">Upgrade to Pro</h1>
          <CText type="r14" color={colors.grayScale500}>
            Get unlimited AI queries, all buckets, and exclusive expert discounts
          </CText>
        </div>

        {/* Plan Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          {plans.map((plan) => {
            const isSelected = selectedPlan === plan.id;
            const isCurrent = currentTier === plan.id;

            return (
              <button
                key={plan.id}
                onClick={() => setSelectedPlan(plan.id)}
                className={`relative p-5 rounded-2xl text-left transition-all duration-200 ${
                  isSelected ? 'scale-[1.02]' : 'hover:scale-[1.01]'
                }`}
                style={{
                  backgroundColor: colors.cardBackground || colors.inputBackground,
                  border: isSelected
                    ? `2px solid ${colors.primary}`
                    : `1px solid ${colors.borderColor}`,
                  boxShadow: isSelected ? colors.shadowLarge : colors.shadowSoft,
                }}
              >
                {/* Badge */}
                {plan.badge && (
                  <div
                    className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full text-xs font-semibold"
                    style={{
                      background: `linear-gradient(135deg, ${colors.primary}, #9F03F2)`,
                      color: '#fff',
                    }}
                  >
                    {plan.badge}
                  </div>
                )}

                {/* Current Badge */}
                {isCurrent && (
                  <div
                    className="absolute top-3 right-3 px-2 py-0.5 rounded text-xs font-medium"
                    style={{
                      backgroundColor: colors.primary + '20',
                      color: colors.primary,
                    }}
                  >
                    Current
                  </div>
                )}

                <CText type="M16" color={colors.textColor} style={{ fontWeight: 600 }}>
                  {plan.name}
                </CText>

                <div className="flex items-baseline gap-1 mt-2 mb-2">
                  <CText type="B24" color={colors.textColor} style={{ fontWeight: 700 }}>
                    {plan.price === 0 ? 'Free' : `Rs.${plan.price}`}
                  </CText>
                  {plan.price > 0 && (
                    <CText type="r12" color={colors.grayScale500}>
                      {plan.period}
                    </CText>
                  )}
                </div>

                <CText type="r12" color={colors.grayScale500}>
                  {plan.description}
                </CText>

                {/* Selection indicator */}
                <div
                  className="mt-4 py-2 rounded-lg text-center text-sm font-semibold transition-all duration-200"
                  style={{
                    backgroundColor: isSelected ? colors.primary : colors.primary + '15',
                    color: isSelected ? '#fff' : colors.primary,
                  }}
                >
                  {isSelected ? 'Selected' : 'Select'}
                </div>
              </button>
            );
          })}
        </div>

        {/* Features Comparison */}
        <div
          className="rounded-2xl overflow-hidden card-shadow"
          style={{
            backgroundColor: colors.cardBackground || colors.inputBackground,
            border: `1px solid ${colors.borderColor}`,
          }}
        >
          <div
            className="p-4 border-b"
            style={{ borderColor: colors.borderColor }}
          >
            <CText type="M16" color={colors.textColor} style={{ fontWeight: 600 }}>
              Compare Features
            </CText>
          </div>

          {/* Feature Rows */}
          {features.map((feature, idx) => (
            <div
              key={feature.name}
              className="grid grid-cols-4 gap-2 p-4 items-center"
              style={{
                backgroundColor: idx % 2 === 0 ? colors.backgroundColor : 'transparent',
                borderBottom: idx < features.length - 1 ? `1px solid ${colors.borderColor}` : 'none',
              }}
            >
              <div className="flex items-center gap-2 col-span-1">
                {React.createElement(feature.icon as any, {
                  size: 16,
                  color: colors.grayScale500,
                })}
                <CText type="r12" color={colors.textColor}>
                  {feature.name}
                </CText>
              </div>
              <div className="text-center">{renderFeatureValue(feature.free)}</div>
              <div className="text-center">{renderFeatureValue(feature.pro)}</div>
              <div className="text-center">{renderFeatureValue(feature.premium)}</div>
            </div>
          ))}

          {/* Column Headers (sticky at bottom for reference) */}
          <div
            className="grid grid-cols-4 gap-2 p-4 border-t"
            style={{
              backgroundColor: colors.primary + '10',
              borderColor: colors.borderColor,
            }}
          >
            <div />
            <div className="text-center">
              <CText type="M12" color={colors.grayScale500}>
                Free
              </CText>
            </div>
            <div className="text-center">
              <CText type="M12" color={colors.primary} style={{ fontWeight: 600 }}>
                Pro
              </CText>
            </div>
            <div className="text-center">
              <CText type="M12" color={colors.grayScale500}>
                Premium
              </CText>
            </div>
          </div>
        </div>

        {/* CTA Button */}
        <div className="mt-8">
          <button
            onClick={() => handleSelectPlan(selectedPlan)}
            disabled={isProcessing || selectedPlan === currentTier}
            className="w-full py-4 rounded-2xl font-semibold text-lg transition-all duration-200 hover:-translate-y-0.5 active:scale-98 disabled:opacity-50"
            style={{
              background:
                selectedPlan === currentTier
                  ? colors.grayScale400
                  : `linear-gradient(135deg, ${colors.primary}, #9F03F2)`,
              color: '#fff',
              boxShadow: selectedPlan === currentTier ? 'none' : colors.shadowLarge,
            }}
          >
            {isProcessing
              ? 'Processing...'
              : selectedPlan === currentTier
              ? 'Current Plan'
              : selectedPlan === 'free'
              ? 'Continue with Free'
              : `Start 7-Day Free Trial - Rs.${plans.find((p) => p.id === selectedPlan)?.price}/mo`}
          </button>

          {selectedPlan !== 'free' && selectedPlan !== currentTier && (
            <CText
              type="r12"
              color={colors.grayScale500}
              style={{ textAlign: 'center', marginTop: 12, display: 'block' }}
            >
              Cancel anytime. No questions asked.
            </CText>
          )}
        </div>

        {/* Trust Section */}
        <div className="mt-8 text-center">
          <div className="flex items-center justify-center gap-6">
            <CText type="r10" color={colors.grayScale500}>
              Secure Payment
            </CText>
            <CText type="r10" color={colors.grayScale500}>
              Instant Activation
            </CText>
            <CText type="r10" color={colors.grayScale500}>
              Cancel Anytime
            </CText>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Pricing;
